import {
  ScreenSpaceEventHandler,
  defined,
  ScreenSpaceEventType,
  JulianDate,
  Color,
  ConstantProperty,
  PropertyBag,
  Cartesian3,
  Cartographic,
  Ellipsoid,
  Rectangle,
  KeyboardEventModifier,
  CallbackProperty,
} from "cesium";
import { getUsernameAndUserIdFormat, removeEpochSuffix, removeFileExtension, showToast } from "../Utility";
import Type from "../Types";
import { disableCameraControls, restoreCameraControls } from "../MapUtility";

export const createAssociationLayerClickHandler = (viewer, _ignored, onAssociation, mapObjectsRef, setMapObjects, { area = true, userData = null } = {}) => {
  // --- Cesium shorthands ---
  const timeNow = () => JulianDate.now();
  const RADIUS_METERS = 100;

  // --- Normalizer ---
  const normalizeKey = (raw) => {
    const noExt = removeFileExtension?.(String(raw || "")) ?? String(raw || "");
    const noEpoch = removeEpochSuffix?.(noExt) ?? noExt;
    return String(noEpoch)
      .toLowerCase()
      .replace(/[\s\-_]/g, "");
  };

  // Read DataSource layer name then normalize
  const getNormalizedLayerKey = (entity) => {
    const owner = entity?.entityCollection?.owner;
    let nm = owner?.layerName ?? owner?.name ?? null;
    if (!nm && viewer?.dataSources) {
      const dsc = viewer.dataSources;
      for (let i = 0; i < dsc.length; i++) {
        const ds = dsc.get(i);
        if (ds?.entities?.contains?.(entity)) {
          nm = ds.layerName ?? ds.name ?? null;
          if (nm) break;
        }
      }
    }
    if (!nm) return "";
    return normalizeKey(nm);
  };

  // Safe read entity.properties[prop]
  const getPropValue = (entity, propName) => {
    const pbag = entity?.properties;
    if (!pbag) return null;
    const p = pbag[propName];
    if (!p) return null;
    try {
      return typeof p.getValue === "function" ? p.getValue(timeNow()) : p;
    } catch {
      return null;
    }
  };

  // Add/append string property as CSV
  const attEditor = getUsernameAndUserIdFormat(userData);

  // Add/append string property as CSV
  const upsertCesiumPropertyString = (entity, key, valueToAdd) => {
    if (!entity.properties) entity.properties = new PropertyBag({});
    if (!(key in entity.properties) || typeof entity.properties[key]?.getValue !== "function") {
      entity.properties.addProperty?.(key);
    }
    const current = getPropValue(entity, key);
    const parts = current
      ? String(current)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
       const val = String(valueToAdd);
    if (!parts.includes(val)) parts.push(val);
    entity.properties[key] = new ConstantProperty(parts.join(","));

    if (attEditor) {
      if (!("att_editor" in entity.properties) || typeof entity.properties.att_editor?.getValue !== "function") {
        entity.properties.addProperty?.("att_editor");
      }
      entity.properties.att_editor = new ConstantProperty(attEditor);
    }
  };

  // Remove one value from CSV property
  const removeCesiumPropertyString = (entity, key, valueToRemove) => {
    const current = getPropValue(entity, key);
    if (!current) return;
    const parts = String(current)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .filter((v) => v !== String(valueToRemove));
    if (!entity.properties) entity.properties = new PropertyBag({});
    if (!(key in entity.properties)) {
      entity.properties.addProperty?.(key);
    }
    entity.properties[key] = new ConstantProperty(parts.join(","));
  };

  // --- Geometry helpers ---
  const getEntityCenter = (entity) => {
    const t = timeNow();

    const posProp = entity?.position;
    if (posProp && typeof posProp.getValue === "function") {
      const c = posProp.getValue(t);
      if (c) return c;
    }

    const ph = entity?.polygon?.hierarchy;
    if (ph && typeof ph.getValue === "function") {
      try {
        const hierarchy = ph.getValue(t);
        const positions = hierarchy?.positions || hierarchy;
        if (Array.isArray(positions) && positions.length > 0) {
          let sx = 0,
            sy = 0,
            sz = 0;
          for (const p of positions) {
            sx += p.x;
            sy += p.y;
            sz += p.z;
          }
          return new Cartesian3(sx / positions.length, sy / positions.length, sz / positions.length);
        }
      } catch {}
    }

    const pl = entity?.polyline?.positions;
    if (pl && typeof pl.getValue === "function") {
      try {
        const positions = pl.getValue(t);
        if (Array.isArray(positions) && positions.length > 0) {
          let sx = 0,
            sy = 0,
            sz = 0;
          for (const p of positions) {
            sx += p.x;
            sy += p.y;
            sz += p.z;
          }
          return new Cartesian3(sx / positions.length, sy / positions.length, sz / positions.length);
        }
      } catch {}
    }

    const rectProp = entity?.rectangle?.coordinates;
    if (rectProp && typeof rectProp.getValue === "function") {
      try {
        const rect = rectProp.getValue(t);
        if (rect) {
          const centerCarto = new Cartographic((rect.west + rect.east) / 2, (rect.south + rect.north) / 2, 0);
          return Ellipsoid.WGS84.cartographicToCartesian(centerCarto);
        }
      } catch {}
    }
    return null;
  };

  const distanceBetweenEntitiesMeters = (a, b) => {
    const ca = getEntityCenter(a);
    const cb = getEntityCenter(b);
    if (!ca || !cb) return Number.POSITIVE_INFINITY;
    return Cartesian3.distance(ca, cb);
  };

  const getAllEntities = (excludeId = null) => {
    const result = [];
    const vEnts = viewer?.entities;
    if (vEnts?.values && Array.isArray(vEnts.values)) {
      for (const e of vEnts.values) {
        if (!e) continue;
        if (excludeId && e.id === excludeId) continue;
        result.push(e);
      }
    }
    const dsc = viewer?.dataSources;
    if (dsc) {
      for (let i = 0; i < dsc.length; i++) {
        const ds = dsc.get(i);
        const ents = ds?.entities?.values;
        if (Array.isArray(ents)) {
          for (const e of ents) {
            if (!e) continue;
            if (excludeId && e.id === excludeId) continue;
            result.push(e);
          }
        }
      }
    }
    return result;
  };

  // --- Style backup/restore ---
  const originalStyles = new Map();
  const highlightEntity = (entity, color) => {
    if (!entity) return;
    if (!originalStyles.has(entity.id)) {
      originalStyles.set(entity.id, {
        polygonMaterial: entity.polygon?.material,
        polylineMaterial: entity.polyline?.material,
        pointColor: entity.point?.color,
      });
    }
    if (entity.polygon) entity.polygon.material = color;
    if (entity.polyline) entity.polyline.material = color;
    if (entity.point) entity.point.color = color;
  };
  const restoreEntity = (entity) => {
    const b = originalStyles.get(entity?.id);
    if (!b) return;
    if (entity.polygon && Object.prototype.hasOwnProperty.call(b, "polygonMaterial")) entity.polygon.material = b.polygonMaterial;
    if (entity.polyline && Object.prototype.hasOwnProperty.call(b, "polylineMaterial")) entity.polyline.material = b.polylineMaterial;
    if (entity.point && Object.prototype.hasOwnProperty.call(b, "pointColor")) entity.point.color = b.pointColor;
  };
  const findEntityById = (id) => {
    const fromViewer = viewer?.entities?.getById?.(id);
    if (fromViewer) return fromViewer;
    const dsc = viewer?.dataSources;
    if (dsc) {
      for (let i = 0; i < dsc.length; i++) {
        const ds = dsc.get(i);
        const ent = ds?.entities?.getById?.(id);
        if (ent) return ent;
      }
    }
    return null;
  };
  const restoreAll = () => {
    for (const [id] of originalStyles.entries()) restoreEntity(findEntityById(id));
    originalStyles.clear();
  };

  const history = [];
  // --- Allowed sets/mapping ---
  const FIRST_LAYERS = new Set(["lanelet", "stopline", "yieldline", "trafficlightbox", "trafficsignbox", "trafficsignface", "trafficlightpole"]);
  const CHILDREN_BY_PARENT = {
    lanelet: new Set(
      [
        "bridge",
        "concretebarrier",
        "crosswalk",
        "curbstone",
        "roadedge",
        "driveway",
        "guardrail",
        "intersection",
        "keepclear",
        "parkind",
        "pedestrianfence",
        "railroadarea",
        "railroad",
        "sidewalk",
        "roadmarking",
        "schoolzone",
        "staticobject",
        "stopline",
        "streetlightpole",
        "trafficcalming",
        "tunnel",
        "underpass",
        "trafficsignbox",
        "trafficlightbox",
        "yieldline,",
        "trafficsignbox",
        "trafficlightpole",
        "trafficsignpole",
        "trafficsignface",
      ].map((s) => s.replace(/,$/, "")),
    ),
    stopline: new Set(["trafficlightbox", "trafficsignbox", "trafficsignface", "trafficlightpole", "trafficsignpole", "trafficlightpole"]),
    yieldline: new Set(["trafficlightbox", "trafficsignbox", "trafficsignface", "trafficlightpole", "trafficsignpole", "trafficlightpole"]),
    trafficlightbox: new Set(["trafficlightbulb", "trafficlightpole", "stopline"]),
    trafficsignbox: new Set(["trafficsignpole", "stopline"]),
    trafficsignface: new Set(["trafficsignpole", "stopline"]),
    trafficlightpole: new Set(["trafficlightbox", "stopline"]),
  };
  const PROP_KEY_BY_PARENT = {
    lanelet: "lane_id",
    stopline: "stop_line_id",
    yieldline: "yield_line_id",
    trafficlightbox: "traffic_light_box_id",
    trafficsignbox: "traffic_sign_box_id",
    trafficsignface: "traffic_sign_face_id",
    trafficlightpole: "traffic_light_pole_id",
    trafficsignpole: "traffic_sign_pole_id",
  };
  // --- State ---
  let firstSelection = null;
  const yellowHighlightedIds = new Set();
  const lockedHighlightIds = new Set();
  let shiftActive = false;

  // --- Rectangle live state ---
  let dragStartPos = null;
  let isDragging = false;
  let suppressNextClick = false;

  let rubberEntity = null;
  let currentRect = new Rectangle();
  const startCarto = new Cartographic();
  const endCarto = new Cartographic();

  // --- Camera control snapshot for lock during rect drag ---
  const originalCameraControls = { current: null };

  // --- Toast ---
  showToast("Tip: Hold SHIFT and drag to box-select. Click to single-select.", Type.NOTIFICATION.INFO);

  // --- Yellow highlight when Shift held ---
  const clearYellowHighlights = () => {
    for (const id of yellowHighlightedIds) {
      const ent = findEntityById(id);
      if (ent) restoreEntity(ent);
    }
    yellowHighlightedIds.clear();
  };
  const highlightNearbyAllowedWithinRadius = () => {
    if (!firstSelection?.entity) return;
    const allowed = CHILDREN_BY_PARENT[firstSelection.parentKey];
    if (!allowed) return;
    const base = firstSelection.entity;
    const baseCenter = getEntityCenter(base);
    if (!baseCenter) return;
    clearYellowHighlights();
    const all = getAllEntities(base.id);
    for (const ent of all) {
      if (!ent || ent.show === false) continue;
      if (lockedHighlightIds.has(ent.id)) continue;
      const layerKey = getNormalizedLayerKey(ent);
      if (!allowed.has(layerKey)) continue;
      const dist = distanceBetweenEntitiesMeters(base, ent);
      if (!isFinite(dist) || dist > RADIUS_METERS) continue;
      highlightEntity(ent, Color.YELLOW.withAlpha(0.7));
      yellowHighlightedIds.add(ent.id);
    }
  };
  const onKeyDown = (e) => {
    if (e?.key !== "Shift") return;
    if (shiftActive) return;
    shiftActive = true;
    if (!firstSelection) return;
    highlightNearbyAllowedWithinRadius();
  };
  const onKeyUp = (e) => {
    if (e?.key !== "Shift") return;
    shiftActive = false;
    clearYellowHighlights();

    // cancel drag if shift released during drag
    if (dragStartPos) {
      dragStartPos = null;
      isDragging = false;
      removeRubberRectangle();
      restoreCameraControls?.(viewer, originalCameraControls); // unlock camera
    }
  };

  document.addEventListener("keydown", onKeyDown, { passive: true });
  document.addEventListener("keyup", onKeyUp, { passive: true });

  // --- Picking helpers for smooth rect ---
  const windowPosToCartesian = (winPos) => {
    if (!winPos) return null;
    const ray = viewer.camera.getPickRay(winPos);
    const globe = viewer.scene.globe;
    return globe?.pick?.(ray, viewer.scene) || viewer.camera.pickEllipsoid(winPos, Ellipsoid.WGS84);
  };
  const updateCurrentRectFromWindow = (p1, p2) => {
    const c1 = windowPosToCartesian(p1);
    const c2 = windowPosToCartesian(p2);
    if (!c1 || !c2) return false;
    const carto1 = Ellipsoid.WGS84.cartesianToCartographic(c1, startCarto);
    const carto2 = Ellipsoid.WGS84.cartesianToCartographic(c2, endCarto);
    const west = Math.min(carto1.longitude, carto2.longitude);
    const east = Math.max(carto1.longitude, carto2.longitude);
    const south = Math.min(carto1.latitude, carto2.latitude);
    const north = Math.max(carto1.latitude, carto2.latitude);
    currentRect.west = west;
    currentRect.east = east;
    currentRect.south = south;
    currentRect.north = north;
    return true;
  };
  const ensureRubberRectangle = () => {
    if (rubberEntity) return;
    rubberEntity = viewer.entities.add({
      rectangle: {
        coordinates: new CallbackProperty(() => currentRect, false),
        material: Color.YELLOW.withAlpha(0.15),
        outline: true,
        outlineColor: Color.YELLOW,
      },
    });
  };
  const removeRubberRectangle = () => {
    if (!rubberEntity) return;
    viewer.entities.remove(rubberEntity);
    rubberEntity = null;
  };

  // Collect entities inside rect
  const cartesianInsideRectangle = (rect, cart) => {
    if (!rect || !cart) return false;
    const carto = Ellipsoid.WGS84.cartesianToCartographic(cart);
    if (!carto) return false;
    const west = rect.west;
    const east = rect.east;
    const lng = carto.longitude;
    const inLon = east >= west ? lng >= west && lng <= east : lng >= west || lng <= east;
    return inLon && carto.latitude >= rect.south && carto.latitude <= rect.north;
  };

  const rectanglesOverlap = (r1, r2) => {
    // handle anti-meridian
    const normalize = (r) => {
      const west = r.west;
      const east = r.east;
      if (east >= west) {
        return [{ west, east }];
      }
      return [
        { west, east: Math.PI },
        { west: -Math.PI, east },
      ];
    };

    const segs1 = normalize(r1);
    const segs2 = normalize(r2);

    const latOverlap = r1.south <= r2.north && r1.north >= r2.south;

    if (!latOverlap) return false;

    for (const a of segs1) {
      for (const b of segs2) {
        const lonOverlap = a.west <= b.east && a.east >= b.west;
        if (lonOverlap) return true;
      }
    }
    return false;
  };

  // point in rect in cartographic
  const cartographicInsideRectangle = (rect, carto) => {
    if (!rect || !carto) return false;
    const west = rect.west;
    const east = rect.east;
    const lon = carto.longitude;
    const lat = carto.latitude;
    // handle anti-meridian
    const inLon = east >= west ? lon >= west && lon <= east : lon >= west || lon <= east;
    return inLon && lat >= rect.south && lat <= rect.north;
  };

  // orientation helper
  const orientation = (ax, ay, bx, by, cx, cy) => {
    const v = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
    if (v > 0) return 1; // ccw
    if (v < 0) return -1; // cw
    return 0; // colinear
  };

  const onSegment = (ax, ay, bx, by, cx, cy) => {
    return Math.min(ax, bx) <= cx && cx <= Math.max(ax, bx) && Math.min(ay, by) <= cy && cy <= Math.max(ay, by);
  };

  const segmentsIntersect2D = (p1, p2, p3, p4) => {
    const o1 = orientation(p1.x, p1.y, p2.x, p2.y, p3.x, p3.y);
    const o2 = orientation(p1.x, p1.y, p2.x, p2.y, p4.x, p4.y);
    const o3 = orientation(p3.x, p3.y, p4.x, p4.y, p1.x, p1.y);
    const o4 = orientation(p3.x, p3.y, p4.x, p4.y, p2.x, p2.y);

    if (o1 !== o2 && o3 !== o4) return true;

    if (o1 === 0 && onSegment(p1.x, p1.y, p2.x, p2.y, p3.x, p3.y)) return true;
    if (o2 === 0 && onSegment(p1.x, p1.y, p2.x, p2.y, p4.x, p4.y)) return true;
    if (o3 === 0 && onSegment(p3.x, p3.y, p4.x, p4.y, p1.x, p1.y)) return true;
    if (o4 === 0 && onSegment(p3.x, p3.y, p4.x, p4.y, p2.x, p2.y)) return true;

    return false;
  };

  // segment vs rect in cartographic lon/lat
  const segmentIntersectsRectangleCartographic = (rect, c1, c2) => {
    if (!rect || !c1 || !c2) return false;

    const p1 = { x: c1.longitude, y: c1.latitude };
    const p2 = { x: c2.longitude, y: c2.latitude };

    // one endpoint inside
    if (cartographicInsideRectangle(rect, c1) || cartographicInsideRectangle(rect, c2)) {
      return true;
    }

    const left = rect.west;
    const right = rect.east;
    const bottom = rect.south;
    const top = rect.north;

    const r1 = { x: left, y: bottom };
    const r2 = { x: right, y: bottom };
    const r3 = { x: right, y: top };
    const r4 = { x: left, y: top };

    if (segmentsIntersect2D(p1, p2, r1, r2)) return true;
    if (segmentsIntersect2D(p1, p2, r2, r3)) return true;
    if (segmentsIntersect2D(p1, p2, r3, r4)) return true;
    if (segmentsIntersect2D(p1, p2, r4, r1)) return true;

    return false;
  };

  const entityIntersectsRectangle = (entity, rect) => {
    const t = timeNow();

    // point / position: same as before
    const posProp = entity?.position;
    if (posProp && typeof posProp.getValue === "function") {
      const c = posProp.getValue(t);
      if (c && cartesianInsideRectangle(rect, c)) return true;
    }

    // polygon: any vertex inside OR any edge intersecting rect
    const ph = entity?.polygon?.hierarchy;
    if (ph && typeof ph.getValue === "function") {
      try {
        const hierarchy = ph.getValue(t);
        const positions = hierarchy?.positions || hierarchy;
        if (Array.isArray(positions) && positions.length > 0) {
          const cartos = [];
          for (const p of positions) {
            const ct = Ellipsoid.WGS84.cartesianToCartographic(p);
            if (!ct) continue;
            cartos.push(ct);
            if (cartographicInsideRectangle(rect, ct)) return true;
          }
          // edges
          for (let i = 0; i < cartos.length - 1; i++) {
            if (segmentIntersectsRectangleCartographic(rect, cartos[i], cartos[i + 1])) return true;
          }
          // close ring
          if (cartos.length > 2 && segmentIntersectsRectangleCartographic(rect, cartos[cartos.length - 1], cartos[0])) {
            return true;
          }
        }
      } catch {}
    }

    // polyline: any vertex inside OR any segment intersecting rect
    const pl = entity?.polyline?.positions;
    if (pl && typeof pl.getValue === "function") {
      try {
        const positions = pl.getValue(t);
        if (Array.isArray(positions) && positions.length > 0) {
          const cartos = [];
          for (const p of positions) {
            const ct = Ellipsoid.WGS84.cartesianToCartographic(p);
            if (!ct) continue;
            cartos.push(ct);
            if (cartographicInsideRectangle(rect, ct)) return true;
          }
          for (let i = 0; i < cartos.length - 1; i++) {
            if (segmentIntersectsRectangleCartographic(rect, cartos[i], cartos[i + 1])) return true;
          }
        }
      } catch {}
    }

    // rectangle: overlap (unchanged)
    const rectProp = entity?.rectangle?.coordinates;
    if (rectProp && typeof rectProp.getValue === "function") {
      try {
        const er = rectProp.getValue(t);
        if (er && rectanglesOverlap(er, rect)) return true;
      } catch {}
    }

    return false;
  };

  const getEntitiesInRectangle = (rect) => {
    const all = getAllEntities(null);
    const hits = [];
    for (const e of all) {
      if (!e || e.show === false) continue;
      if (entityIntersectsRectangle(e, rect)) hits.push(e);
    }
    return hits;
  };

  // Batch associate for lanelet parents
  const associateInRectangle = (rect) => {
    if (!rect) return;

    const inside = getEntitiesInRectangle(rect);
    if (inside.length === 0) {
      showToast("No entities in area.", Type.NOTIFICATION.WARNING);
      return;
    }

    const parents = inside.filter((e) => getNormalizedLayerKey(e) === "lanelet");
    if (parents.length === 0) {
      showToast("No LANELET in area. Draw again or click.", Type.NOTIFICATION.INFO);
      return;
    }

    const allowedChildLayers = new Set(["stopline", "yieldline", "intersection", "trafficsignbox", "trafficlightbox", "trafficsignpole", "trafficlightpole"]);

    const children = inside.filter((e) => allowedChildLayers.has(getNormalizedLayerKey(e)));

    const propKey = PROP_KEY_BY_PARENT.lanelet;
    const parentIds = [];
    for (const p of parents) {
      const pid = getPropValue(p, "id");
      if (pid != null && String(pid) !== "") parentIds.push(String(pid));
    }
    if (parentIds.length === 0) {
      showToast("LANELET(s) missing properties.id", Type.NOTIFICATION.WARNING);
      return;
    }

    for (const p of parents) {
      highlightEntity(p, Color.RED.withAlpha(0.75));
      lockedHighlightIds.add(p.id);
      try {
        const temp = [...(mapObjectsRef?.current || [])];
        for (const obj of temp) {
          if (obj?.dataSource?.entities?.contains?.(p)) {
            obj.is_edited = true;
            break;
          }
        }
        setMapObjects?.(temp);
      } catch {}
    }

    for (const ch of children) {
      for (const pid of parentIds) upsertCesiumPropertyString(ch, propKey, pid);
      highlightEntity(ch, Color.BLUE.withAlpha(0.7));
      lockedHighlightIds.add(ch.id);
      const parentEntityIds = parents.map((p) => p.id);
      history.push({
        childId: ch.id,
        propKey,
        parentIds: parentIds.map(String),
        parentEntityIds, // all parents affected in this op
      });

      try {
        const temp = [...(mapObjectsRef?.current || [])];
        for (const obj of temp) {
          if (obj?.dataSource?.entities?.contains?.(ch)) {
            obj.is_edited = true;
            break;
          }
        }
        setMapObjects?.(temp);
      } catch {}
      onAssociation?.({
        parentLayer: "lanelet",
        parentId: parentIds.join(","),
        childLayer: getNormalizedLayerKey(ch),
        childEntityId: ch.id,
        propKey,
      });
    }

    if (children.length === 0) {
      showToast("LANELET found. No allowed children in area.", Type.NOTIFICATION.INFO);
    } else {
      showToast(`Associated ${children.length} child(ren) to ${parentIds.length} lanelet(s).`, Type.NOTIFICATION.SUCCESS);
    }
  };

  // --- Mouse input ---
  const handler = new ScreenSpaceEventHandler(viewer?.scene?.canvas);

  // SHIFT + LEFT_DOWN → start rect gesture
  handler.setInputAction(
    (movement) => {
      dragStartPos = movement?.position || movement;
      isDragging = false;
      suppressNextClick = false;
      disableCameraControls?.(viewer, originalCameraControls); // lock camera
    },
    ScreenSpaceEventType.LEFT_DOWN,
    KeyboardEventModifier.SHIFT,
  );

  // SHIFT + MOUSE_MOVE → live rectangle
  handler.setInputAction(
    (movement) => {
      if (!dragStartPos) return;
      const cur = movement?.endPosition || movement;
      if (!isDragging && Math.hypot(cur.x - dragStartPos.x, cur.y - dragStartPos.y) > 5) isDragging = true;
      if (!isDragging) return;
      ensureRubberRectangle();
      if (updateCurrentRectFromWindow(dragStartPos, cur) && viewer.scene.requestRenderMode) {
        viewer.scene.requestRender(); // force a frame in requestRenderMode
      }
    },
    ScreenSpaceEventType.MOUSE_MOVE,
    KeyboardEventModifier.SHIFT,
  );

  // SHIFT + LEFT_UP → finalize rect
  handler.setInputAction(
    (movement) => {
      const wasDragging = isDragging;
      const endPos = movement?.position || movement;

      // build immutable rectangle BEFORE clearing dragStartPos
      let finalRect = null;
      if (wasDragging && updateCurrentRectFromWindow(dragStartPos, endPos)) {
        finalRect = Rectangle.fromRadians(currentRect.west, currentRect.south, currentRect.east, currentRect.north);
      }

      // finalize state
      dragStartPos = null;
      isDragging = false;

      removeRubberRectangle();
      restoreCameraControls?.(viewer, originalCameraControls); // unlock camera

      if (wasDragging && finalRect) {
        suppressNextClick = true;
        associateInRectangle(finalRect);
      }
    },
    ScreenSpaceEventType.LEFT_UP,
    KeyboardEventModifier.SHIFT,
  );

  // Click flow (no modifier). Works alongside rectangle.
  handler.setInputAction((click) => {
    if (suppressNextClick) {
      suppressNextClick = false;
      return;
    }
    const picked = viewer?.scene?.pick?.(click?.position);
    if (!defined(picked)) return;
    const entity = picked.id || picked.primitive?.id;
    if (!entity) return;

    const layerKey = getNormalizedLayerKey(entity);

    if (!firstSelection) {
      if (!FIRST_LAYERS.has(layerKey)) {
        showToast("First selection must be on LANELET / STOP_LINE / YIELD_LINE / TRAFFIC_LIGHT_BOX / TRAFFIC_SIGN_BOX / TRAFFIC_SIGN_FACE / TRAFFIC_LIGHT_POLE", Type.NOTIFICATION.WARNING);
        return;
      }
      const pid = getPropValue(entity, "id");
      if (pid == null || pid === "") {
        showToast("Selected base entity has no properties.id", Type.NOTIFICATION.WARNING);
        return;
      }
      const propKey = PROP_KEY_BY_PARENT[layerKey];
      if (!propKey) {
        showToast("Unsupported base layer.", Type.NOTIFICATION.WARNING);
        return;
      }

      firstSelection = { entity, parentKey: layerKey, parentId: String(pid), propKey };
      highlightEntity(entity, Color.RED.withAlpha(0.75));
      lockedHighlightIds.add(entity.id);

      const allowedLayers = Array.from(CHILDREN_BY_PARENT[layerKey] || []);
      const allowedList = allowedLayers.length > 0 ? allowedLayers.join(", ") : "no layers available";
      showToast(`Now select related entities from allowed layers: ${allowedList}`, Type.NOTIFICATION.INFO);

      if (shiftActive) highlightNearbyAllowedWithinRadius();
      return;
    }

    const allowed = CHILDREN_BY_PARENT[firstSelection.parentKey];
    if (!allowed || !allowed.has(layerKey)) {
      showToast("This layer is not allowed for the current base selection.", Type.NOTIFICATION.WARNING);
      return;
    }

    if (yellowHighlightedIds.has(entity.id)) yellowHighlightedIds.delete(entity.id);

    upsertCesiumPropertyString(entity, firstSelection.propKey, firstSelection.parentId);

    highlightEntity(entity, Color.BLUE.withAlpha(0.7));
    lockedHighlightIds.add(entity.id);

    history.push({
      childId: entity.id,
      propKey: firstSelection.propKey,
      parentIds: [String(firstSelection.parentId)],
      parentEntityIds: [firstSelection.entity.id], // parent entity ids
    });

    try {
      const temp = [...(mapObjectsRef?.current || [])];
      for (const obj of temp) {
        if (obj?.dataSource?.entities?.contains?.(entity)) {
          obj.is_edited = true;
          break;
        }
      }
      setMapObjects?.(temp);
    } catch (e) {
      console.warn?.("[createAssociationLayerClickHandler] Failed to flag is_edited:", e);
    }

    showToast("Associated.", Type.NOTIFICATION.SUCCESS);
    onAssociation?.({
      parentLayer: firstSelection.parentKey,
      parentId: firstSelection.parentId,
      childLayer: layerKey,
      childEntityId: entity.id,
      propKey: firstSelection.propKey,
    });
  }, ScreenSpaceEventType.LEFT_CLICK);

  const undoLast = () => {
    if (history.length === 0) {
      showToast("Nothing to undo.", Type.NOTIFICATION.INFO);
      return;
    }
    const last = history.pop();

    // child
    const child = findEntityById(last.childId);
    if (child) {
      if (last.propKey && Array.isArray(last.parentIds)) {
        for (const pid of last.parentIds) {
          removeCesiumPropertyString(child, last.propKey, pid);
        }
      }
      restoreEntity(child);
      lockedHighlightIds.delete(child.id);
    }

    // parents
    if (Array.isArray(last.parentEntityIds)) {
      for (const pid of last.parentEntityIds) {
        const parent = findEntityById(pid);
        if (!parent) continue;
        restoreEntity(parent);
        lockedHighlightIds.delete(parent.id);
      }
    }

    if (viewer.scene.requestRenderMode) viewer.scene.requestRender();

    showToast("Last association undone.", Type.NOTIFICATION.SUCCESS);
  };

  // Public destroy
  return {
    destroy: () => {
      try {
        handler?.destroy();
      } finally {
        document.removeEventListener("keydown", onKeyDown);
        document.removeEventListener("keyup", onKeyUp);
        clearYellowHighlights();
        removeRubberRectangle();
        if (originalCameraControls.current) restoreCameraControls?.(viewer, originalCameraControls);
        restoreAll();
        yellowHighlightedIds.clear();
        lockedHighlightIds.clear();
        firstSelection = null;
        shiftActive = false;
        dragStartPos = null;
        isDragging = false;
        suppressNextClick = false;
        history.length = 0;
      }
    },
    undoLast,
  };
};
